import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {uid} from 'react-uid';
import {Box, Checkbox, FormControl, Radio} from '@tenancy.nz/design-system';
import {StyledBox} from './SelectionGroup.styled';

function SelectionGroup({
  disabled,
  errorMessage,
  light,
  label,
  name,
  options,
  onChange,
  required,
  showError,
  type,
  value,
  vertical,
}) {
  const [selection, setSelection] = useState(value || '');

  const handleChange = e => {
    setSelection(e.target.value);
    onChange(e.target.value);
  };

  const Component = type === 'radio' ? Radio : Checkbox;

  const textProps = {
    ...(light && {color: 'inherit'}),
  };
  return (
    <StyledBox light={light}>
      <FormControl
        disabled={disabled}
        errorMessage={errorMessage}
        fullWidth
        label={label}
        name={name}
        required={required}
        showError={showError}>
        <Box
          display="flex"
          flexWrap="wrap"
          margin="10px 0"
          sx={{flexDirection: {xs: 'column', sm: vertical ? 'column' : 'row'}}}>
          {options.map(option => (
            <Box key={uid(option)} margin={vertical ? '4px 0' : '0 5px'}>
              <Component
                checked={selection === option.value}
                color="tertiary"
                disabled={disabled}
                filledOnChecked={type === 'checkbox'}
                label={option.label}
                name={name}
                value={option.value}
                textProps={textProps}
                onChange={handleChange}
              />
            </Box>
          ))}
        </Box>
      </FormControl>
    </StyledBox>
  );
}

SelectionGroup.propTypes = {
  /**
   * If `true`, will make fields unselectable.
   */
  disabled: PropTypes.bool,

  /**
   * Text represents the error message.
   */
  errorMessage: PropTypes.string,

  /**
   * If `true`, will use light theme settings
   */
  light: PropTypes.bool,

  /**
   * Text to display as lable for the group
   */
  label: PropTypes.string.isRequired,

  /**
   * HTML attribute of checkobx element.
   */
  name: PropTypes.string.isRequired,

  /**
   * On change event handler.
   */
  onChange: PropTypes.func.isRequired,

  /**
   * List of options to render as list.
   */
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    })
  ).isRequired,

  /**
   * If `true`,  will be marked as required.
   */
  required: PropTypes.bool,

  /**
   * If `true`,  will show error message.
   */
  showError: PropTypes.bool,

  /**
   * If `true`,  will show error message.
   */
  type: PropTypes.oneOf(['checkbox', 'radio']),

  /**
   * A selected value.
   */
  value: PropTypes.string,

  /**
   * If true, Stack all items
   */
  vertical: PropTypes.bool,
};

SelectionGroup.defaultProps = {
  disabled: false,
  errorMessage: '',
  light: false,
  required: false,
  showError: false,
  type: 'checkbox',
  value: '',
  vertical: false,
};

export default SelectionGroup;
